import { useState } from "react";

export interface ThresholdFailure {
  type: string;
  dimensionId?: string | null;
  dimensionName?: string | null;
  score?: number | null;
  overall?: number | null;
  count?: number | null;
  min?: number | null;
}

export interface ThresholdFlagProps {
  passing: boolean | null;
  overridden: boolean;
  overrideReason?: string | null;
  failures?: ThresholdFailure[] | null;
  size?: "sm" | "md";
}

export function formatThresholdFailure(failure: ThresholdFailure): string {
  switch (failure.type) {
    case "critical_below_min":
      return `${failure.dimensionName ?? "Critical dimension"} scored ${failure.score}/5 (rule requires ${failure.min})`;
    case "overall_below_min":
      return `Overall ${failure.overall} below required ${failure.min}`;
    case "strengths_below_min":
      return `Only ${failure.count} strengths evidenced (rule requires ${failure.min})`;
    default:
      return failure.type;
  }
}

export function ThresholdFlag({
  passing,
  overridden,
  overrideReason,
  failures,
  size = "md",
}: ThresholdFlagProps) {
  const [open, setOpen] = useState(false);
  const sizeClass = size === "sm" ? "px-1.5 py-0.5 text-[10px]" : "px-2 py-0.5 text-xs";

  if (overridden) {
    return (
      <span
        className={`inline-flex items-center gap-1 rounded-full bg-amber-100 font-medium text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 ${sizeClass}`}
        title={overrideReason ?? undefined}
      >
        Override
      </span>
    );
  }

  if (passing === true) {
    return (
      <span
        className={`inline-flex items-center gap-1 rounded-full bg-emerald-50 font-medium text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300 ${sizeClass}`}
      >
        Above threshold
      </span>
    );
  }

  if (passing === false) {
    const lines = (failures ?? []).map(formatThresholdFailure);
    return (
      <span className="relative inline-block">
        <button
          type="button"
          onMouseEnter={() => setOpen(true)}
          onMouseLeave={() => setOpen(false)}
          onFocus={() => setOpen(true)}
          onBlur={() => setOpen(false)}
          className={`inline-flex items-center gap-1 rounded-full bg-red-100 font-medium text-red-800 dark:bg-red-900/30 dark:text-red-300 ${sizeClass}`}
          aria-describedby={lines.length > 0 ? "threshold-failure-list" : undefined}
        >
          Below threshold
        </button>
        {open && lines.length > 0 && (
          <div
            id="threshold-failure-list"
            role="tooltip"
            className="absolute left-0 top-full z-10 mt-1 w-64 rounded border border-border bg-card p-2 text-xs text-text-secondary shadow"
          >
            <ul className="list-disc pl-4 space-y-1">
              {lines.map((l) => (
                <li key={l}>{l}</li>
              ))}
            </ul>
            <p className="mt-2 border-t border-border pt-1 text-[10px]">
              <a
                href="/app/help/score-bands#thresholds"
                target="_blank"
                rel="noopener noreferrer"
                className="text-link hover:underline"
              >
                How threshold rules work →
              </a>
            </p>
          </div>
        )}
      </span>
    );
  }

  return null;
}
